//@flow

import React, { Fragment } from 'react';
import { Col } from 'reactstrap';

import { format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { withRouter } from 'react-router-dom';
import { Collapse, Button } from 'reactstrap';

import { useToggle } from '../../../hooks';
import FlightSummaryCard from './FlightSummaryCard';
import { useFlightReviews } from '../Common';

const formatDate = date => format(date, 'D MMMM YYYY');

type Props = {
  type: string,
};

export default function SelectedFlightReview(props: Props) {
  const { type } = props;
  const flight = useFlightReviews()[type];
  
  const { totalAmount, currency, adult, child, infant, flightDate } = flight;
  const departureOrReturn = type === 'outbound' ? 'DEPARTURE' : 'RETURN';

  return (
    <Col md="6">
      <div className="white-panel flight-sum">
        <h2>{`${departureOrReturn} FLIGHT SUMMARY`} </h2>
        <h5 className="date-pass-count">
          {formatDate(flightDate)} |{' '}
          {renderPassengerCount(adult, child, infant)}
        </h5>
        <FlightSummaryCard flight={flight} />
        <FlightDetails flight={flight} />

        <hr />

        <ChangeFlightsButton />

        <hr />
        <div className="fare-list">
          <FlightFare flight={flight} />
          <FlightFees flight={flight} />

          <div className="d-flex justify-content-between fare-cont">
            <h6>TOTAL AMOUNT</h6>
            <h6 className="sum-price">
              {currency} {totalAmount}
            </h6>
          </div>
        </div>
      </div>
    </Col>
  );
}

function renderPassengerCount(adult, child, infant) {
  adult = Number(adult);
  child = Number(child);
  infant = Number(infant);
  let count = '';
  if (adult === 1) count = `${adult} Adult`;
  if (adult > 1) count = `${adult} Adults`;
  if (child !== 0) {
    if (child === 1) count = count + `, ${child} Child`;
    if (child > 1) count = count + `, ${child} Children`;
  }
  if (infant !== 0) {
    if (infant === 1) count = count + ` & ${infant} Infant`;
    if (infant > 1) count = count + ` & ${infant} Infants`;
  }

  return count;
}

function FlightDetails({ flight }) {
  const {
    airline,
    aircraftType,
    flightNo,
    flightClassCode,
    freeBaggage,
  } = flight;
  const { on, toggle } = useToggle(false);

  const values = [
    { title: 'Airline', value: airline },
    { title: 'Aircraft Type', value: aircraftType },
    { title: 'Flight Number', value: flightNo },
    { title: 'Flight Class', value: flightClassCode },
    { title: 'Free Baggage', value: freeBaggage },
  ];

  return (
    <React.Fragment>
      <a className="collapse-btn" onClick={toggle}>
        View Flight and Baggage Detail <i className="icon icon-hg-chev-down" />
      </a>
      <Collapse isOpen={on} className="cont-list">
        {values.map(({ title, value }) => (
          <div className="d-flex justify-content-between" key={title}>
            <p>{title}</p>
            <p>{value}</p>
          </div>
        ))}
      </Collapse>
    </React.Fragment>
  );
}

export const getFlightFees = flight => {
  if (!flight || isEmpty(flight)) return { total: 0, tax: 0, fuelSurcharge: 0 };

  const { fuelSurcharge: fs, tax, adult, child } = flight;
  const noOfPeople = Number(adult) + Number(child);
  const fuelSurcharge = Number(fs) * noOfPeople;
  const totalTax = Number(tax) * noOfPeople;
  return {
    total: totalTax + fuelSurcharge,
    tax: totalTax,
    fuelSurcharge,
  };
};

const FlightFees = ({ flight }) => {
  const fees = getFlightFees(flight);
  const { currency } = flight;
  const { on, toggle } = useToggle(false);
  return (
    <Fragment>
      <div className="d-flex justify-content-between fare-cont">
        <a className="collapse-btn" onClick={toggle}>
          TAXES AND FEES <i className="icon icon-hg-chev-down" />
        </a>
        <p className="sum-price">
          {currency} {fees.total.toLocaleString()}
        </p>
      </div>
      <Collapse isOpen={on} className="cont-list">
        <div className="d-flex justify-content-between">
          <p>fuel charge</p>
          <p>
            {currency} {fees.fuelSurcharge.toLocaleString()}
          </p>
        </div>
        <div className="d-flex justify-content-between">
          <p>tax</p>
          <p>
            {currency} {fees.tax.toLocaleString()}
          </p>
        </div>
      </Collapse>
    </Fragment>
  );
};

const getFlightFare = flight => {
  if (!flight || isEmpty(flight))
    return { total: 0, adultFare: 0, childFare: 0, infantFare: 0 };

  const {
    adult,
    child,
    infant,
    adultFare: af,
    childFare: cf,
    infantFare: inf,
  } = flight;

  const adultFare = Number(adult) * Number(af);
  const childFare = Number(child) * Number(cf);
  const infantFare = Number(infant) * Number(inf);
  return {
    total: adultFare + childFare + infantFare,
    adultFare,
    childFare,
    infantFare,
  };
};

const FlightFare = ({ flight }) => {
  const { on, toggle } = useToggle(false);
  const flightFare = getFlightFare(flight);
  const { adult, child, currency } = flight;

  return (
    <Fragment>
      <div className="d-flex justify-content-between fare-cont">
        <a className="collapse-btn" onClick={toggle}>
          FLIGHT FARE <i className="icon icon-hg-chev-down" />
        </a>
        <p className="sum-price">
          {currency} {flightFare.total.toLocaleString()}
        </p>
      </div>
      <Collapse isOpen={on} className="cont-list">
        <div className="d-flex justify-content-between">
          <p>{adult} Adults</p>
          <p>
            {currency} {flightFare.adultFare.toLocaleString()}
          </p>
        </div>
        {Boolean(Number(child)) && (
          <div className="d-flex justify-content-between">
            <p>{child} child</p>
            <p>
              {currency} {flightFare.childFare.toLocaleString()}
            </p>
          </div>
        )}
      </Collapse>
    </Fragment>
  );
};

const ChangeFlights = ({ history, location }) => {
  const { pathname, search } = location;
  return (
    <div
      onClick={() => {
        history.push({
          pathname: `/${pathname.split('/')[1]}`,
          search,
        });
      }}
    >
      <Button color="trans">
        <i className="icon icon-hg-chev-left" /> Change Flight
      </Button>
    </div>
  );
};

const ChangeFlightsButton = withRouter(ChangeFlights);



// WEBPACK FOOTER //
// ./src/screens/Flights/Review/SelectedFlightReview.js